import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { ApiError } from "@/api/client";
import {
  useIncidents,
  useIncidentReportPdfUrl,
} from "@/api/queries/incidents";
import { useIncidentsCsvExport } from "@/api/queries/reports";
import { incidentDisplayName, type IncidentSummary } from "@/api/models";
import { useCurrentUser } from "@/api/queries/me";
import { formatDateTime } from "@/lib/format";
import { safeReportUrl } from "@/lib/reports";

export function ReportsPage() {
  const me = useCurrentUser();
  const tz = me.data?.department?.timezone;
  const incidents = useIncidents({
    status: "all",
    sort: "dispatched_at_desc",
    pageSize: 100,
  });
  const [selected, setSelected] = useState<IncidentSummary | null>(null);

  const items = incidents.data?.items ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Reports</h1>
        <p className="text-sm text-muted-foreground">
          Every incident on record. Select one to open and read its report PDF.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>
            {incidents.data
              ? `${incidents.data.total} incident${
                  incidents.data.total === 1 ? "" : "s"
                }`
              : "Incidents"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {incidents.isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-6" />
              <Skeleton className="h-6" />
            </div>
          ) : incidents.isError ? (
            <p className="p-6 text-sm text-destructive">
              Failed to load incidents. Check your connection and try again.
            </p>
          ) : items.length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Incident</Th>
                  <Th>Dispatched</Th>
                  <Th>Status</Th>
                  <Th>
                    <span className="sr-only">Report</span>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((i) => {
                  const active = selected?.id === i.id;
                  return (
                    <Tr key={i.id}>
                      <Td className="font-medium">{incidentDisplayName(i)}</Td>
                      <Td className="text-sm">
                        {formatDateTime(i.dispatched_at, tz)}
                      </Td>
                      <Td>
                        <Badge
                          variant={i.status === "active" ? "warning" : "outline"}
                        >
                          {i.status}
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          variant={active ? "secondary" : "outline"}
                          size="sm"
                          onClick={() => setSelected(active ? null : i)}
                        >
                          {active ? "Hide report" : "View report"}
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              No incidents on record yet.
            </p>
          )}
        </CardContent>
      </Card>

      {selected ? (
        <ReportViewer
          key={selected.id}
          incidentId={selected.id}
          title={incidentDisplayName(selected)}
        />
      ) : null}

      <CsvExportCard />
    </div>
  );
}

/**
 * Resolves the presigned PDF URL for the selected incident and renders it
 * inline so a chief/admin can read the report without leaving the page. The
 * `key={incidentId}` in the parent remounts this on each selection, so the
 * effect re-fetches a fresh signed URL per incident. A 404 means the report
 * has not been finalized/rendered yet — surfaced as a friendly empty state.
 */
function ReportViewer({
  incidentId,
  title,
}: {
  incidentId: string;
  title: string;
}) {
  const pdf = useIncidentReportPdfUrl(incidentId);
  const [url, setUrl] = useState<string | null>(null);
  const mutate = pdf.mutate;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setUrl(null);
    mutate(undefined, { onSuccess: (res) => setUrl(safeReportUrl(res.url)) });
  }, [incidentId, mutate]);

  // The viewer renders below a long incident list, so on selection it would
  // otherwise mount off-screen — the user sees only the button flip and assumes
  // nothing happened. Pull it into view so "View report" visibly opens the report.
  useEffect(() => {
    containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [incidentId]);

  return (
    <Card ref={containerRef} className="scroll-mt-6">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle>{title}: report</CardTitle>
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary hover:underline"
          >
            Open in new tab
          </a>
        ) : null}
      </CardHeader>
      <CardContent>
        {pdf.isPending ? (
          <Skeleton className="h-[640px] w-full" />
        ) : pdf.isError ? (
          <p className="text-sm text-muted-foreground">
            {pdf.error instanceof ApiError && pdf.error.status === 404
              ? "No report PDF yet. A report is generated when the incident is closed and finalized."
              : pdf.error instanceof Error
                ? pdf.error.message
                : "Could not load the report PDF."}
          </p>
        ) : url ? (
          <iframe
            title={`Incident report — ${title}`}
            src={url}
            className="h-[640px] w-full rounded-md border border-border"
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Secondary export: spreadsheet of incidents for a date range. Kept alongside
 * the report browser because chiefs use it for record-keeping outside the app.
 */
function CsvExportCard() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const csv = useIncidentsCsvExport();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Incident CSV export</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            From
            <Input
              type="date"
              className="mt-1 w-44"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="text-sm">
            To
            <Input
              type="date"
              className="mt-1 w-44"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <Button
            disabled={csv.isPending}
            onClick={() =>
              csv.mutate({ from: from || undefined, to: to || undefined })
            }
            data-testid="export-incidents-csv"
          >
            {csv.isPending ? "Exporting…" : "Export"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Leave a date blank for an open-ended range. Clear both to export the
          full history.
        </p>
        {csv.isSuccess ? (
          <p className="text-xs text-muted-foreground">
            Exported <strong>{csv.data.count}</strong>{" "}
            {csv.data.count === 1 ? "incident" : "incidents"} to{" "}
            <code>{csv.data.filename}</code>.
          </p>
        ) : null}
        {csv.isError ? (
          <p className="text-xs text-destructive">
            {csv.error instanceof Error ? csv.error.message : "Export failed."}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
